import AddButton from "components/Button/AddButton";
import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DropdownStatusList, EmptyString, FormTypeAdd, PageSize, StatusCodeActive, StatusCodeDelete } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import SearchButton from "components/Button/SearchButton";
import Dropdown from "components/Input/Dropdown";
import { addSpaceEvery4, GetStatus, removeNonNumeric } from "utils/helpers";
import RevisionIcon from "assets/icon/RevisionIcon";
import { useCallApi } from "api/apiHooks";
import { useModal } from "utils/context/ModalContext";
import Pagination from "components/Input/PaginationButton";
import { FilterCard, GridDeleteButton, GridEditButton, MainBodyCard, PagesTitle } from "components/Cards";
import Select from 'react-select'
import DataTable from "components/DataTable";

export default function BeneficiaryAccount(){
    let navigate = useNavigate()
    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchBeneficiaryList, fetchDeleteBeneficiary, fetchBankList } = useCallApi()
    const firstLoad = useRef(true)
    const debounceRef = useRef(null)

    const [beneficiaryList, setBeneficiaryList] = useState([])
    const [bankId, setBankId] = useState(EmptyString)
    const [bankOptions, setBankOptions] = useState([])
    const [accountNo, setAccountNo] = useState(EmptyString)
    const [accountName, setAccountName] = useState(EmptyString)
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "accountName",
            header: "Nama Account"
        },
        {
            key: "accountNo",
            header: "Account No",
            render: (row) => addSpaceEvery4(row.accountNo)
        },
        {
            key: "bankName",
            header: "Bank",
            render: (row) => row.bankCode + ' - ' + row.bankName
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.beneficiaryId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.beneficiaryId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]

    async function handlePageChange(newIndex) {
        try {
            showLoading()

            const res = await fetchBeneficiaryList(accountNo, accountName, bankId === EmptyString ? "0" : bankId, status, PageSize, parseInt(newIndex))

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setBeneficiaryList(res)
            setTotalPages(total);
            setPageIndex(newIndex);
        } finally {
            hideLoading();
        }
    }

    const searchBtnClick = () => {
        handlePageChange(0)
    }
    const addBtnClick = () => {
        navigate("/beneficiary-account/form?beneficiaryId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (beneficiaryId) => {
        navigate("/beneficiary-account/form?beneficiaryId=" + beneficiaryId + "&formType=Edit")
    }
    const deleteBtnClick = async (beneficiaryId, status) => {
        const isConfirmed = await confirm()

        if (isConfirmed) {
            showLoading()

            const res = await fetchDeleteBeneficiary(beneficiaryId, status)

            if (!res) return

            showSuccess()
            handlePageChange(pageIndex)
        }
    }

    const getBankList = async () => {
        const res = await fetchBankList('', '', '1', 10000, 0)
        if (!res) return

        const data = res.data.listData
        const arr = [
            { label: "All", value: "" },
            ...data.map(item => ({
                label: `${item.bankCode} - ${item.bankName}`,
                value: String(item.bankId)
            }))
        ]

        setBankOptions(arr)
        setBankId(arr[0].value)
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                showLoading()

                await getBankList()

                handlePageChange(0)

                firstLoad.current = false
            }
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[accountName, accountNo, bankId, status])

    return(
        <MainBodyCard>
            <PagesTitle title='Beneficiary Account' />

            <div className="flex flex-col sm:flex-row gap-5 justify-between mt-5">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                    <FilterCard title='Account Name' width={'w-full sm:w-56'}>
                        <input className="input" value={accountName} onChange={(e) => { setAccountName(e.target.value) }} />
                    </FilterCard>
                    <FilterCard title='Account No' width={'w-full sm:w-56'}>
                        <input className="input" value={accountNo} onChange={(e) => { setAccountNo(e.target.value) }} />
                    </FilterCard>
                    <FilterCard title='Bank' width={'w-full sm:w-72'}>
                        <Select className="w-full" value={bankOptions.find(opt => opt.value === bankId)} options={bankOptions} onChange={(e) => { setBankId(e.value) }} placeholder={"All"} />
                    </FilterCard>
                    <FilterCard title='Status' width={'w-full sm:w-32'}>
                        <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => { setStatus(e.value) }} />
                    </FilterCard>
                </div>
            </div>

            <div className="mt-3 flex w-full justify-end">
                <AddButton onClick={addBtnClick} title="Tambah Beneficiary Account" />
            </div>
           
            <div className="hidden sm:block mt-5">
                <DataTable
                    columns={columns}
                    data={beneficiaryList?.data?.listData ?? []}
                />
            </div>

            <div className="block sm:hidden mt-5">
                <div className="flex flex-col gap-3">
                    {beneficiaryList?.data?.listData?.map((data, i) => {
                        return(
                            <div className="border rounded-md bg-white shadow flex justify-between p-3">
                                <div className="flex flex-col">
                                    <label>{data.accountName.toUpperCase()}</label>
                                    <label>{data.bankCode + " - " + data.bankName}</label>
                                    <label>{data.accountNo}</label>
                                </div>
                                <div className="flex flex-col justify-between">
                                    <button className="button flex gap-2" onClick={() => editBtnClick(data.beneficiaryId)}>
                                        <EditIcon className="w-4 fill-white" />
                                    </button>
                                    <button className={`button flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.beneficiaryId, data.status)}>
                                        <TrashIcon className="w-4 fill-white" />
                                    </button>
                                    <button className={`button flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.beneficiaryId, data.status)}>
                                        <RevisionIcon className="w-4 fill-white" />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}